import { Box, Paper, styled, Typography } from "@mui/material";
import { LoginFlow, UpdateLoginFlowBody } from "@ory/kratos-client";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Configuration } from "../../../../../config";
import { UiMessages } from "../../../components";
import { hasVisibleNodes } from "../../../components/ui/ui";
import HooloovooSrc from "../images/hooloovoo-logo.png";
import { OidcForm } from "./OidcForm";
import { PasswordForm } from "./PasswordForm";
import { SectionDivider } from "./SectionDivider";

const FullPageContainer = styled(Box)({
  width: "100vw",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const StyledImg = styled("img")(({ theme }) => ({
  position: "absolute",
  left: "50%",
  transform: "translateX(-50%)",
  marginTop: 20,
  width: "35%",
  maxWidth: "100%",
  height: "auto",

  [theme.breakpoints.between("sm", "md")]: {
    width: "auto",
  },
}));

const AccentedText = styled("span")(() => ({
  color: "black",
  fontWeight: 600,
  fontSize: "20px",
  lineHeight: "28px",
}));

export interface SignInFormProps {
  flow: LoginFlow;
  onAttemptLogin: (values: UpdateLoginFlowBody) => Promise<void>;
  errorCode: number | null;
}

export const SignInForm: React.FC<SignInFormProps> = ({
  flow,
  onAttemptLogin,
  errorCode,
}) => {
  const { t } = useTranslation("anonymous");
  const navigate = useNavigate();

  if (!hasVisibleNodes(flow.ui.nodes)) {
    navigate(flow.return_to || "/");
  }

  const oidcEnabled = Configuration.login.includes("oidc");
  const passwordEnabled = Configuration.login.includes("password");
  const providers = Configuration.providers;
  const loginLogoSrc = Configuration.branding.loginLogo;
  const loginLogoBackgroundColor = Configuration.branding.backgroundColor;

  return (
    <FullPageContainer>
      <Paper
        sx={(theme) => ({
          width: 351,
          minHeight: 256,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          padding: 0.5,
          boxShadow: "0px 4px 8px rgba(20, 17, 16, 0.15);",
          borderRadius: "16px",
          backdropFilter: "blur(4px)",

          [theme.breakpoints.down("md")]: {
            width: "100%",
            height: "100vh",
            borderRadius: 0,
            gap: "10px",
          },
        })}
      >
        <Box
          sx={(theme) => ({
            height: "144px",
            backgroundColor: loginLogoBackgroundColor
              ? "orange.light"
              : "inherit",
            overflow: "hidden",
            borderRadius: "16px",
            position: "relative",

            [theme.breakpoints.down("md")]: {
              flex: "1 1 auto",
              maxHeight: "180px",
            },
          })}
        >
          <StyledImg src={loginLogoSrc} alt="" role="presentation" />
        </Box>
        <Box
          sx={(theme) => ({
            paddingX: 3,
            paddingY: 2,
            [theme.breakpoints.down("md")]: {
              flex: "1 1 auto",
            },
          })}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <Typography variant="h5">
              <Trans
                t={t}
                i18nKey="login.welcome"
                components={{
                  Accent: <AccentedText />,
                }}
              />
            </Typography>
          </Box>

          <Typography variant="h6" align="center">
            {t("login.tagline")}
          </Typography>

          {flow.ui.messages && <UiMessages messages={flow.ui.messages} />}

          <Box sx={{ marginTop: "24px" }} />

          {oidcEnabled && (
            <OidcForm
              handleSubmit={onAttemptLogin}
              ui={flow.ui}
              showProviders={providers}
            />
          )}
          {passwordEnabled && (
            <>
              <Box sx={{ marginTop: "24px" }} />

              {oidcEnabled && <SectionDivider>{t("login.or")}</SectionDivider>}

              <Box sx={{ marginTop: "24px" }} />
              <PasswordForm handleSubmit={onAttemptLogin} ui={flow.ui} />
            </>
          )}
          {/* <Box sx={{ marginTop: "24px", textAlign: "center" }}>
            <Typography
              variant="body3"
              component="div"
              sx={(theme) => ({
                color: theme.palette.grey[600],
              })}
            >
              Korišćenjem aplikacije prihvatate <br />
              <StyledLink href="/terms-of-service">
                Uslove korišćenja
              </StyledLink>{" "}
              i{" "}
              <StyledLink href="/privacy-policy">
                Politiku privatnosti
              </StyledLink>
              .
            </Typography>
          </Box> */}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              marginTop: "24px",
            }}
          >
            <Typography
              variant="body3"
              sx={(theme) => ({
                color: theme.palette.grey[600],
              })}
            >
              Powered by
            </Typography>
            <Box
              component="img"
              src={HooloovooSrc}
              alt="Hooloovoo Logo"
              width={"97px"}
              height={"12px"}
            />
          </Box>
        </Box>
      </Paper>
    </FullPageContainer>
  );
};
